import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import Avatar from '@app/components/dashboard/Avatar';
import { AuthState, useAuth } from '@app/hooks/useAuth';
import { NavigationItem } from '@app/hooks/useCommonData';
import { useLayout } from '@app/hooks/useLayout';
import { cn, maskEmail } from '@app/libs/utils';

import PlusIcon from '../icons/PlusIcon';

interface AuthMenuProps {
  className?: string;
  isSimple?: boolean;
}

export const AuthMenu: React.FC<AuthMenuProps> = ({ className, isSimple = false }) => {
  const { isAuth, info, logout } = useAuth();
  const router = useRouter();
  const navigationItems: NavigationItem[] = [
    { label: 'Quản lý giao dịch', href: '/dashboard/transactions' },
    { label: 'Quản lý thông tin', href: '/dashboard' },
    { label: 'Quản lý bài đăng', href: '/dashboard/listing' },
    { label: 'Quản lý tài khoản', href: '/dashboard/profile' },
    { label: 'Đổi mật khẩu', href: '/dashboard/profile/change-password' },
    ...(process.env.NODE_ENV === 'development' ? [{ label: 'Test Zone', href: '/test' }] : []),
  ];
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as any)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleLogout = async () => {
    setOpen(false);
    await logout(false);
  };

  return (
    <>
      {isAuth === AuthState.AUTH ? (
        <>
          {isSimple ? (
            <>
              <div className="flex flex-col py-4 gap-5 w-full border-b border-gray-200">
                {navigationItems.map(item => (
                  <Link
                    key={item.label}
                    href={item.href}
                    className={cn(
                      item.active ? 'text-blue-800' : 'text-gray-900 hover:text-blue-800',
                      'text-md font-bold uppercase transition-colors duration-200'
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              <a
                onClick={handleLogout}
                className="bg-blue-800 py-2 px-4 text-white text-sm font-[500] rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-200 grow-1 cursor-pointer"
              >
                Đăng xuất
              </a>
            </>
          ) : (
            <>
              <Link
                href="/dashboard/listing/new"
                className={cn(
                  'text-sm font-bold transition-colors duration-200 flex items-center gap-2',
                  'border border-blue-800 rounded-lg px-4 py-2',
                  'hover:bg-blue-900 hover:text-white transition-colors duration-200 cursor-pointer'
                )}
              >
                <PlusIcon className="w-4 h-4" />
                Tạo tin đăng
              </Link>
              <div
                ref={menuRef}
                className={cn('relative', className)}
                onMouseEnter={() => setOpen(true)}
              >
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={() => setOpen(v => !v)}
                  className="flex items-center gap-3 rounded-full border border-gray-200 px-2 py-1 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Avatar
                    userInfo={info}
                    className="w-8 h-8 bg-gray-200 rounded-full overflow-hidden text-lg"
                  />
                  <span className="hidden sm:block text-sm font-medium text-gray-900">
                    {info?.alias ||
                      info?.fullName ||
                      (info?.email ? maskEmail(info.email) : 'Tài khoản')}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={cn(
                      'h-4 w-4 text-gray-500 transition-transform',
                      open ? 'rotate-180' : ''
                    )}
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {open && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 z-50"
                  >
                    <div className="py-2">
                      {navigationItems.map(item => (
                        <Link
                          key={item.label}
                          href={item.href}
                          className={cn(
                            'block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-800 cursor-pointer'
                          )}
                          onClick={() => setOpen(false)}
                          role="menuitem"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                    <div className="border-t border-gray-100" />
                    <div className="py-2">
                      <a
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-700 cursor-pointer"
                        onClick={handleLogout}
                        role="menuitem"
                      >
                        Đăng xuất
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <Link
            href="/auth/register"
            className="bg-white flex items-center justify-center text-sm font-bold text-gray-900 hover:bg-gray-50 transition-colors duration-200 grow-1"
          >
            Đăng ký
          </Link>

          <Link
            href="/auth/login"
            onClick={() => {
              sessionStorage.setItem('returnUrl', router.asPath);
              router.push('/auth/login');
            }}
            className="bg-blue-800 py-2 px-4 text-white text-sm font-[500] rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-200 grow-1"
          >
            Đăng nhập
          </Link>
          <Link
            href="/auth/login?returnUrl=/dashboard/listing/new"
            className={cn(
              'text-sm font-bold transition-colors duration-200 flex items-center gap-2',
              'border border-blue-800 rounded-lg px-4 py-2',
              'hover:bg-blue-900 hover:text-white transition-colors duration-200 cursor-pointer'
            )}
          >
            <PlusIcon className="w-4 h-4" />
            Tạo tin đăng
          </Link>
        </>
      )}
    </>
  );
};
